import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";

interface MobileStoryFullBleedCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  priority?: boolean;
}

/**
 * Full-bleed editorial card — Guardian / Times front page style.
 * Image spans edge-to-edge (no inset, no rounding), ~50% of screen.
 * Headline always reads first; category is small and muted at the bottom.
 */
export function MobileStoryFullBleedCard({
  story,
  exploreBase,
  priority = false,
}: MobileStoryFullBleedCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group -mx-1 block no-underline active:opacity-95"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Full-bleed image — no rounding, edge to edge */}
      <div className="relative aspect-[3/2] w-full bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt={story.headline}
          className="h-full w-full object-cover"
          fill
          priority={priority}
          sizes="100vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />

        {/* Only "New" badge on image — no category */}
        {story.isNew && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-inline-shadow)] backdrop-blur-[10px]">
            <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
          </div>
        )}
      </div>

      {/* Editorial text block — headline first */}
      <div className="flex flex-col px-1 pt-3 pb-1">
        <h3
          className="mb-2 line-clamp-4 text-pretty font-bold text-[1.3rem] text-[var(--explore-text-primary)] leading-[1.2]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        <p className="mb-2 line-clamp-2 text-[0.88rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {story.summary}
        </p>

        {/* Category + tag + time — small, muted, bottom */}
        <div className="flex items-center gap-1.5 text-[0.68rem] text-[var(--explore-text-muted)]">
          {story.categoryDisplayName && (
            <>
              <span className="font-medium">
                {story.categoryEmoji && (
                  <span className="mr-0.5">{story.categoryEmoji}</span>
                )}
                {story.categoryDisplayName}
              </span>
              <span>·</span>
            </>
          )}
          {primaryTag && (
            <>
              <span className="font-medium">{primaryTag.displayName}</span>
              <span>·</span>
            </>
          )}
          <span>{story.timeAgo}</span>
        </div>
      </div>
    </Link>
  );
}
